// Non-destructive sync for ONE jsonb column of the shared `user_preferences` row
// — the collections a store writes whole (supplement stack, grooming items,
// vitals, journal prompts, home widgets, care practices, …).
//
// ─── The bug this replaces ──────────────────────────────────────────────────
// `saveUserPreference({ supplement_stack: stack })` wrote this tab's copy of the
// whole list. A tab open since yesterday therefore put back an item deleted on
// another device the moment it changed anything, and dropped what other devices
// had added. A counter (`fitness_direct_xp`) was written as an absolute value, so
// two devices each adding XP kept only one of the grants.
//
// ─── What this does instead ─────────────────────────────────────────────────
// The column is a syncDoc document `{ value }` (lib/syncDoc.ts): a write
// re-reads the column and applies only what THIS tab changed since it last
// agreed with the cloud, by the column's rule (`{ records: 'id' }` for a list,
// a delta for a counter, …). The compare-and-swap token is the shared row's
// `updated_at`, so any other preference write in between simply costs a retry —
// no migration, no new table.
//
// ⚠️ The base lives in memory: these stores are not persisted, they load from
// the cloud on every boot. A load REPLACES the value and the base (what the old
// loads did); an edit made before any load records its base first.
//
// ─── A tab that comes back catches up (`loadedFor`) ─────────────────────────
// As the blob docs do (lib/blobFocusRefresh.ts): it asks for the row's
// `updated_at`, and only when that moved reads its column and folds it in —
// unsaved edits here kept and written, everything else the cloud's.
// ⚠️ The row is SHARED, so its revision moves with ANY preference write (this
// tab's own, another column's): after one, each column re-reads once and finds
// nothing changed. The probe is one request for every column
// (readPreferencesRevision), so a focus where nothing moved costs one timestamp.

import type { BlobTransport, BlobSnapshot } from './syncBlob';
import { createDocSync, sameJson, type DocBase, type MergeRule } from './syncDoc';
import { loadedFor as loadedForAccount, refreshBlobOnFocus } from './blobFocusRefresh';
import {
  readPreferenceColumn,
  readPreferencesRevision,
  writePreferenceColumn,
  type PreferenceColumn,
} from './userPreferences';

export type { PreferenceColumn };

/**
 * One column as a blob: `data` is `{ value }`, or null while the column was never
 * saved (no row, or the column is null). `encode` maps the value to what the
 * column stores.
 */
export function preferenceColumnTransport(
  column: PreferenceColumn,
  encode: (value: unknown) => unknown = (v) => v,
): BlobTransport {
  return {
    async read(): Promise<BlobSnapshot | null> {
      const got = await readPreferenceColumn(column);
      if (!got) return null;
      return { updatedAt: got.updatedAt, data: got.value === null ? null : { value: got.value }, user: got.user };
    },
    // The ROW's revision — one shared request, whichever columns ask.
    version: () => readPreferencesRevision(),
    write: (payload: { value: unknown }, expectedUpdatedAt: string | null, forUser?: string) =>
      writePreferenceColumn(column, encode(payload.value), expectedUpdatedAt, forUser),
  };
}

export interface PreferenceSync<V> {
  /** Request a write (debounced, merged into the cloud's current column). */
  schedule: () => void;
  /** Await the pending write (tests; sign-out). */
  flush: () => Promise<void>;
  /** Write now; resolves whether it landed (for a store that rolls back on failure). */
  saveNow: () => Promise<boolean>;
  /**
   * A fresh LOAD: decode `columnValue` (raw, as read — null if never saved) and
   * make it the base. Returns the value for the store to set. Replaces, never
   * merges, exactly as the loads always did. `updatedAt` = the revision of the
   * row it was read from (`row.updated_at`): the tab holds that revision, so its
   * first refresh costs a timestamp, not a read. Omitted = unknown.
   */
  load: (columnValue: unknown, updatedAt?: string | null) => V;
  /** Call BEFORE a mutation: a tab that never loaded takes what it held as its base. */
  recordBase: (current: V) => void;
  /** Forget the base and the revision held (sign-out / account switch). */
  reset: () => void;
  /** The value the cloud is known to hold — loaded, or last confirmed. A rollback target. */
  confirmed: () => V | undefined;
}

export interface PreferenceSyncOptions<V> {
  column: PreferenceColumn;
  /** How the value merges when this tab and the cloud both changed it. */
  rule: MergeRule;
  /** Raw column value (null = never saved) → the store's value. */
  decode: (columnValue: unknown) => V;
  /** The store's value → what the column stores (default: as is). */
  encode?: (value: V) => unknown;
  /** The store's current value. */
  read: () => V;
  /** Put a merged value into the store (it already differs from `read()`). */
  adopt: (value: V) => void;
  /** For the error log. */
  label: string;
  /**
   * The account the store's value was LOADED for (its `initializedUserId`;
   * null = not loaded). The store catches up when the tab comes back — only
   * while loaded, and for the account signed in NOW: another account's column
   * must never be folded into it. ⚠️ It is also the sync's owner
   * (lib/syncBlob): a write while ANOTHER account is signed in sends nothing —
   * this account's value must never be merged into the next account's column.
   */
  loadedFor: () => string | null;
}

export function createPreferenceSync<V>(options: PreferenceSyncOptions<V>): PreferenceSync<V> {
  const { column, rule, decode, read, adopt, label } = options;
  const encode = (options.encode ?? ((v: V) => v)) as (value: unknown) => unknown;
  let base: DocBase<{ value: V }> | null = null;
  let confirmedValue: V | undefined;

  const doc = createDocSync<{ value: V }>({
    transport: preferenceColumnTransport(column, encode),
    owner: options.loadedFor,
    rules: { value: rule },
    hydrate: (data) => ({
      value: decode(data && typeof data === 'object' && 'value' in data ? (data as { value: unknown }).value : null),
    }),
    read: () => ({ value: read() }),
    readBase: () => base,
    adopt: (next, nextBase) => {
      // ⚠️ Reset (signed out, account switched) while a cycle was out: what it
      // brings back belongs to nobody on screen now — it must not land.
      if (base === null) return;
      base = nextBase;
      confirmedValue = next.value; // a confirmed write corrects this to exactly what landed
      if (!sameJson(next.value, read())) adopt(next.value);
    },
    onWritten: (written) => { if (base !== null) confirmedValue = written.value; },
    onError: (error) => console.error(`Failed to save ${label}:`, error),
    // The row is shared by every preference: an unrelated write in between is a retry, not a conflict.
    maxAttempts: 6,
  });

  const ready = loadedForAccount(options.loadedFor);
  refreshBlobOnFocus(`user_preferences:${column}`, () => doc.refresh(ready));

  return {
    schedule: doc.schedule,
    flush: doc.flush,
    saveNow: doc.saveNow,
    load(columnValue, updatedAt = null) {
      const value = decode(columnValue);
      base = doc.baseOf({ value });
      confirmedValue = value;
      doc.noteLoaded(updatedAt);
      return value;
    },
    recordBase(current) {
      if (base === null) base = doc.baseOf({ value: current });
    },
    reset() {
      base = null;
      confirmedValue = undefined;
      // A revision held before the reset says nothing about what shows now.
      doc.noteLoaded(null);
    },
    confirmed: () => confirmedValue,
  };
}
